import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Globe, Building2, Search, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';

export default function ProcessShareModal({ process, open, onOpenChange, onUpdate }) {
  const { company } = useAuth();
  const [isGlobal, setIsGlobal] = useState(process?.is_global || false);
  const [spaces, setSpaces] = useState([]);
  const [selectedSpaces, setSelectedSpaces] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch available spaces and current shares
  const fetchData = useCallback(async () => {
    if (!company?.id || !process?.id) return;

    setLoading(true);
    try {
      // Fetch all company spaces
      const { data: spacesData, error: spacesError } = await supabase
        .from('projects')
        .select('id, name, description')
        .eq('company_id', company.id)
        .eq('is_archived', false)
        .order('name');

      if (spacesError) throw spacesError;
      setSpaces(spacesData || []);

      // Fetch current shares for this process
      const { data: sharesData, error: sharesError } = await supabase.rpc(
        'get_process_shares',
        { p_workflow_id: process.id }
      );

      if (sharesError) throw sharesError;

      // Set selected spaces from current shares
      const sharedSpaceIds = new Set((sharesData || []).map(s => s.project_id));
      setSelectedSpaces(sharedSpaceIds);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load sharing settings');
    } finally {
      setLoading(false);
    }
  }, [company?.id, process?.id]);

  useEffect(() => {
    if (open) {
      setIsGlobal(process?.is_global || false);
      fetchData();
    }
  }, [open, process, fetchData]);

  // Filter spaces by search
  const filteredSpaces = spaces.filter(space => {
    if (!searchQuery) return true;
    return space.name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Toggle space selection
  const toggleSpace = (spaceId) => {
    setSelectedSpaces(prev => {
      const next = new Set(prev);
      if (next.has(spaceId)) {
        next.delete(spaceId);
      } else {
        next.add(spaceId);
      }
      return next;
    });
  };

  // Select all visible spaces
  const selectAll = () => {
    setSelectedSpaces(new Set(filteredSpaces.map(s => s.id)));
  };

  // Deselect all
  const deselectAll = () => {
    setSelectedSpaces(new Set());
  };

  // Save changes
  const handleSave = async () => {
    if (!company?.id) {
      toast.error('Company not found');
      return;
    }

    setSaving(true);
    try {
      // Use RPC to update sharing settings (bypasses RLS)
      const { error } = await supabase.rpc('update_process_sharing', {
        p_workflow_id: process.id,
        p_company_id: company.id,
        p_is_global: isGlobal,
        p_space_ids: isGlobal ? null : Array.from(selectedSpaces)
      });

      if (error) throw error;

      toast.success('Sharing settings updated');
      onUpdate?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving shares:', error);
      toast.error('Failed to update sharing settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Manage Process Sharing</DialogTitle>
          <DialogDescription>
            Choose which spaces can run this process. Global processes are available to all spaces.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Global Toggle */}
          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-100">
            <div className="flex items-center gap-3">
              <Globe className="h-5 w-5 text-blue-600" />
              <div>
                <Label htmlFor="global-toggle" className="font-medium text-gray-900">
                  Available to all spaces
                </Label>
                <p className="text-sm text-gray-500">
                  All spaces in your company can run this process
                </p>
              </div>
            </div>
            <Switch
              id="global-toggle"
              checked={isGlobal}
              onCheckedChange={setIsGlobal}
            />
          </div>

          {/* Space Selection (only shown when not global) */}
          {!isGlobal && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-medium">Select spaces</Label>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={selectAll}>
                    Select all
                  </Button>
                  <Button variant="ghost" size="sm" onClick={deselectAll}>
                    Clear
                  </Button>
                </div>
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search spaces..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>

              {/* Space List */}
              <ScrollArea className="h-64 border rounded-lg">
                {loading ? (
                  <div className="p-3 space-y-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="flex items-center gap-3 p-2">
                        <Skeleton className="h-4 w-4" />
                        <Skeleton className="h-4 w-32" />
                      </div>
                    ))}
                  </div>
                ) : filteredSpaces.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    {searchQuery ? 'No spaces match your search' : 'No spaces available'}
                  </div>
                ) : (
                  <div className="p-1">
                    {filteredSpaces.map(space => (
                      <div
                        key={space.id}
                        className={`flex items-center gap-3 p-3 rounded-md cursor-pointer hover:bg-gray-50 transition-colors ${
                          selectedSpaces.has(space.id) ? 'bg-primary/5' : ''
                        }`}
                        onClick={() => toggleSpace(space.id)}
                      >
                        <Checkbox
                          checked={selectedSpaces.has(space.id)}
                          onCheckedChange={() => toggleSpace(space.id)}
                        />
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <span className="font-medium text-gray-900 truncate">
                            {space.name}
                          </span>
                        </div>
                        {selectedSpaces.has(space.id) && (
                          <Check className="h-4 w-4 text-primary flex-shrink-0" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>

              {/* Selection Summary */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">
                  {selectedSpaces.size} of {spaces.length} spaces selected
                </span>
                {selectedSpaces.size > 0 && (
                  <Badge variant="secondary">
                    {selectedSpaces.size} selected
                  </Badge>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
